/**
 * The per-query filter chips shared by every panel that groups an MCP action's
 * fan-out.
 *
 * One tool call fans out into a path per query, so the LLM requests panel, the
 * retrieved-deals table and the retrieved-chunks table all group by query and
 * all offer the same "which query?" filter. Kept in one place so they cannot
 * drift into looking or behaving differently.
 */
import type { JSX } from "react";

export interface QueryChip {
  /** The path's place in the query array the caller sent, 0-based. */
  position: number;
  label: string;
  count: number;
}

function chipClass(active: boolean): string {
  const state = active
    ? "bg-purple-100 text-purple-800 border-purple-300"
    : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50";
  return `px-3 py-1 rounded-full text-xs font-medium border max-w-xs truncate ${state}`;
}

export default function QueryFilterChips({
  chips,
  total,
  selected,
  onSelect,
  ariaLabel,
}: {
  chips: QueryChip[];
  /** Count behind the "All queries" chip — the whole panel, not the sum of chips. */
  total: number;
  selected: number | null;
  onSelect: (position: number | null) => void;
  ariaLabel: string;
}): JSX.Element {
  return (
    // role="group", because a bare div carries no role for the label to name —
    // assistive tech would announce the chips without saying what set they form.
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label={ariaLabel}>
      {/*
        aria-pressed, because these are toggles: which one is on is otherwise
        carried by colour alone, which a screen reader cannot relay.
      */}
      <button
        type="button"
        onClick={() => onSelect(null)}
        aria-pressed={selected === null}
        className={chipClass(selected === null)}
      >
        All queries ({total})
      </button>
      {chips.map((chip) => (
        <button
          key={chip.position}
          type="button"
          title={chip.label}
          onClick={() => onSelect(chip.position)}
          aria-pressed={selected === chip.position}
          className={chipClass(selected === chip.position)}
        >
          {chip.label} ({chip.count})
        </button>
      ))}
    </div>
  );
}
